import type { BankDocumentAllocationTargetType } from '../Types/bankDocument.types';

export interface AllocationSummaryInput {
  amount: number;
  invoiceId?: number | null;
  reservationId?: number | null;
  allocations?: Array<{
    targetType: BankDocumentAllocationTargetType;
    allocatedAmount: number;
  }> | null;
  allocationCount?: number | null;
  allocatedAmount?: number | null;
  remainingAmount?: number | null;
  allocationTypeSummary?: BankDocumentAllocationTargetType | null;
}

export interface BankDocumentAllocationSummary {
  allocationCount: number;
  allocatedAmount: number;
  remainingAmount: number;
  allocationTypeSummary: BankDocumentAllocationTargetType | null;
}

const roundAmount = (value: number): number => Math.round(value * 100) / 100;

const resolveAllocationTypeSummary = (
  allocations: NonNullable<AllocationSummaryInput['allocations']>
): BankDocumentAllocationTargetType | null => {
  if (allocations.length === 0) {
    return null;
  }

  const firstType = allocations[0].targetType;
  return allocations.every((allocation) => allocation.targetType === firstType) ? firstType : null;
};

export const buildBankDocumentAllocationSummary = (
  input: AllocationSummaryInput
): BankDocumentAllocationSummary => {
  const allocations = input.allocations ?? [];
  const explicitCount = input.allocationCount ?? undefined;
  const explicitAllocatedAmount = input.allocatedAmount ?? undefined;
  const explicitRemainingAmount = input.remainingAmount ?? undefined;
  const explicitTypeSummary = input.allocationTypeSummary ?? undefined;

  if (
    explicitCount !== undefined &&
    explicitAllocatedAmount !== undefined &&
    explicitRemainingAmount !== undefined
  ) {
    return {
      allocationCount: explicitCount,
      allocatedAmount: roundAmount(explicitAllocatedAmount),
      remainingAmount: roundAmount(Math.max(0, explicitRemainingAmount)),
      allocationTypeSummary:
        explicitCount > 0 ? (explicitTypeSummary ?? resolveAllocationTypeSummary(allocations)) : null,
    };
  }

  if (allocations.length > 0) {
    const allocatedAmount = roundAmount(
      allocations.reduce((sum, allocation) => sum + allocation.allocatedAmount, 0)
    );

    return {
      allocationCount: explicitCount ?? allocations.length,
      allocatedAmount,
      remainingAmount: input.amount > 0 ? roundAmount(Math.max(0, input.amount - allocatedAmount)) : 0,
      allocationTypeSummary: explicitTypeSummary ?? resolveAllocationTypeSummary(allocations),
    };
  }

  if (input.amount > 0 && (input.invoiceId || input.reservationId)) {
    return {
      allocationCount: 1,
      allocatedAmount: input.amount,
      remainingAmount: 0,
      allocationTypeSummary: input.invoiceId ? 'invoice' : 'reservation',
    };
  }

  return {
    allocationCount: 0,
    allocatedAmount: 0,
    remainingAmount: input.amount > 0 ? input.amount : 0,
    allocationTypeSummary: null,
  };
};

export const formatBankDocumentAllocationSummaryLabel = (
  summary: BankDocumentAllocationSummary
): string => {
  if (summary.allocationCount === 0) {
    return '-';
  }

  if (!summary.allocationTypeSummary) {
    return `Alokacje x${summary.allocationCount}`;
  }

  const familyLabel = summary.allocationTypeSummary === 'invoice' ? 'Faktury' : 'Rezerwacje';
  return `${familyLabel} x${summary.allocationCount}`;
};
